import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal, ViewEncapsulation } from "@angular/core";
import { TranslocoPipe } from "@jsverse/transloco";

import { IconComponent } from "../../display/icon/icon.component";
import { ToastIconPreset } from "./models/toast.model";
import { AZM_TOAST_DATA } from "./models/toast.tokens";

const TOAST_ICON_PRESETS: Record<string, ToastIconPreset> = {
	"toast-success": {
		icon: "toast-success",
		iconClass: "azm-toast__icon",
	},
	"toast-error": {
		icon: "toast-error",
		iconClass: "azm-toast__icon",
	},
	"toast-warning": {
		icon: "toast-warning",
		iconClass: "azm-toast__icon",
	},
	"toast-info": {
		icon: "toast-info",
		iconClass: "azm-toast__icon",
	},
};

const DEFAULT_TOAST_ICON_PRESET: ToastIconPreset = {
	icon: "toast-default",
	iconClass: "azm-toast__icon",
};

@Component({
	selector: "azm-toast-instance",
	imports: [TranslocoPipe, IconComponent],
	templateUrl: "./toast.component.html",
	styleUrl: "./style/toast.component.scss",
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		"[attr.data-toast-id]": "data.toastId",
		"[class.toast-out]": "isRemoving()",
		"[class.azm-toast-host--paused]": "isPaused()",
		"[style.--animation-duration]": 'data.config.easeTime + "ms"',
		"[style.--animation-easing]": '"ease-in"',
		"[style.--toast-progress-duration]": 'data.config.timeOut + "ms"',
		"(mouseenter)": "pauseTimer()",
		"(mouseleave)": "resumeTimer()",
		"(click)": "onTap()",
	},
})
export class AzmToastComponent implements OnInit {
	readonly data = inject(AZM_TOAST_DATA);

	readonly isRemoving = signal(false);
	readonly isPaused = signal(false);

	readonly iconPreset = computed((): ToastIconPreset => {
		return TOAST_ICON_PRESETS[this.data.toastType] ?? DEFAULT_TOAST_ICON_PRESET;
	});

	protected readonly toastTitle = computed(() => this.data.title?.trim() ?? "");
	protected readonly toastMessage = computed(() => this.data.message?.trim() ?? "");

	#hideTimeoutId: ReturnType<typeof setTimeout> | null = null;
	#remainingMs = 0;
	#hideAt = 0;

	ngOnInit(): void {
		this.#remainingMs = this.data.config.timeOut;
		this.#scheduleHide(this.#remainingMs);
	}

	remove(): void {
		if (this.isRemoving()) {
			return;
		}

		this.#clearHideTimeout();
		this.isRemoving.set(true);

		setTimeout(() => this.data.onRemove(this.data.toastId), this.data.config.easeTime);
	}

	pauseTimer(): void {
		if (this.isPaused() || this.isRemoving()) {
			return;
		}

		this.isPaused.set(true);
		this.#remainingMs = Math.max(0, this.#hideAt - Date.now());
		this.#clearHideTimeout();
	}

	resumeTimer(): void {
		if (!this.isPaused() || this.isRemoving()) {
			return;
		}

		this.isPaused.set(false);
		this.#scheduleHide(this.#remainingMs);
	}

	onTap(): void {
		if (this.data.config.tapToDismiss) {
			this.remove();
		}
	}

	#scheduleHide(delayMs: number): void {
		this.#hideAt = Date.now() + delayMs;
		this.#hideTimeoutId = setTimeout(() => this.remove(), delayMs);
	}

	#clearHideTimeout(): void {
		if (this.#hideTimeoutId != null) {
			clearTimeout(this.#hideTimeoutId);
			this.#hideTimeoutId = null;
		}
	}
}
